import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const SCROLL_THRESHOLD = 600

export default function BackToTop() {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  // True when the fixed button overlaps the (dark) footer — flips chrome to ivory.
  const [overFooter, setOverFooter] = useState(false)

  useEffect(() => {
    function onScroll() {
      setVisible(window.scrollY > SCROLL_THRESHOLD)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (!visible) return
    const footer = document.querySelector('footer')
    if (!footer) return
    const obs = new IntersectionObserver(
      (entries) => setOverFooter(entries[0]?.isIntersecting ?? false),
      // Tight rootMargin so the flip only fires when the footer actually reaches
      // the bottom-right region the button occupies (~96px tall slot).
      { rootMargin: '0px 0px -85% 0px', threshold: 0 },
    )
    obs.observe(footer)
    return () => obs.disconnect()
  }, [visible])

  function scrollToTop() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' })
    const main = document.getElementById('main-content')
    if (main) main.focus({ preventScroll: true })
  }

  // Light, recessive chrome so it reads as the SECONDARY action beneath the
  // gold chat launcher (a second heavy dark blob next to the gold FAB clashed).
  // Over the dark footer it flips to a translucent ivory outline so it stays
  // legible without competing with the gold launcher.
  const chrome = overFooter
    ? 'bg-mocha/40 text-ivory border-ivory/30 hover:bg-mocha/60 hover:border-gold/60 hover:text-gold backdrop-blur-sm'
    : 'bg-ivory text-mocha border-mocha/15 hover:border-gold/60 hover:text-gold'

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          onClick={scrollToTop}
          aria-label={t('system.backToTop', 'Back to top')}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          // Stacked ABOVE the concierge chat launcher, which anchors at
          // `bottom: calc(1.25rem + safe-area-inset-bottom)`, w-12 h-12 (3rem),
          // right-5, centre 44px from the edge. We MUST mirror the same
          // safe-area inset or the two collide on phones with a home indicator
          // (the bug seen on mobile). Bottom = launcher base 1.25rem + its
          // 3rem height + a 0.75rem gap = 5rem, plus the inset. This 44px button
          // is centred over the 48px launcher via right-[22px] (44px centre).
          style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
          className={`fixed right-[22px] z-50 w-11 h-11 rounded-full border shadow-md transition-colors flex items-center justify-center ${chrome}`}
        >
          <ArrowUp size={18} aria-hidden="true" />
        </motion.button>
      )}
    </AnimatePresence>
  )
}
